import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useBlueprintColumns } from '@hooks/useQuery'
import {
  getSelectCol,
  getSelectAlias,
  makeSelectColumn,
  isValidAlias
} from 'src/utils/blueprintUtils'
import type { SelectNode, SelectColumn } from 'src/types/queries'

import './SelectEditor.css'

interface SelectEditorProps {
  node: SelectNode
  onChange: (node: SelectNode) => void
}

export default function SelectEditor({ node, onChange }: SelectEditorProps) {
  const intl = useIntl()
  const { columns } = useBlueprintColumns(node.source)

  const [advanced, setAdvanced] = useState(() =>
    node.columns.some((c) => typeof c !== 'string')
  )

  const setColumn = (index: number, value: SelectColumn) => {
    const newColumns = node.columns.map((c, i) => (i === index ? value : c))
    onChange({ ...node, columns: newColumns })
  }

  const handleColChange = (index: number, newCol: string) => {
    const prev = node.columns[index]
    const prevCol = getSelectCol(prev)
    const prevAlias = getSelectAlias(prev)
    const keepAlias = prevAlias !== prevCol ? prevAlias : undefined
    setColumn(index, makeSelectColumn(newCol, keepAlias))
  }

  const handleAliasChange = (index: number, newAlias: string) => {
    const col = getSelectCol(node.columns[index])
    setColumn(index, makeSelectColumn(col, newAlias))
  }

  const handleAddColumn = () => {
    onChange({ ...node, columns: [...node.columns, makeSelectColumn('')] })
  }

  const handleRemoveColumn = (index: number) => {
    const newColumns = node.columns.filter((_, i) => i !== index)
    onChange({ ...node, columns: newColumns })
  }

  const canRemove = node.columns.length > 1

  return (
    <div id="select-editor">
      <header>
        <h3>
          <FormattedMessage id={`operation-editor.option.${node.type}`} />
        </h3>
        <label className="advanced-toggle">
          <input
            type="checkbox"
            checked={advanced}
            onChange={(e) => setAdvanced(e.target.checked)}
          />
          <FormattedMessage id="select-editor.advanced" />
        </label>
      </header>
      {advanced && (
        <div className="columns-title">
          <h4>
            <FormattedMessage id="select-editor.advanced.column" />
          </h4>
          <h4>
            <FormattedMessage id="select-editor.advanced.alias" />
          </h4>
        </div>
      )}

      <ul className="columns-list">
        {node.columns.map((entry, index) => {
          const col = getSelectCol(entry)
          const effectiveAlias = getSelectAlias(entry)
          const displayAlias = effectiveAlias === col ? '' : effectiveAlias
          const aliasInvalid = !isValidAlias(effectiveAlias)

          const otherSelected = node.columns
            .filter((_, i) => i !== index)
            .map(getSelectCol)
          const availableColumns = columns.filter(
            (c) => !otherSelected.includes(c)
          )

          return (
            <li key={index} className="column">
              <select
                className="col-select"
                value={col}
                onChange={(e) => handleColChange(index, e.target.value)}
              >
                <option disabled hidden value="">
                  {intl.formatMessage({
                    id: 'select-editor.column.placeholder'
                  })}
                </option>
                {availableColumns.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              {advanced && (
                <input
                  type="text"
                  className={`alias-input${aliasInvalid ? ' invalid' : ''}`}
                  value={displayAlias}
                  placeholder={
                    col ||
                    intl.formatMessage({
                      id: 'select-editor.alias.placeholder'
                    })
                  }
                  onChange={(e) => handleAliasChange(index, e.target.value)}
                />
              )}

              <button
                type="button"
                className="remove-column"
                onClick={() => handleRemoveColumn(index)}
                disabled={!canRemove}
              >
                <span className="icon icon-bin" />
              </button>
            </li>
          )
        })}
      </ul>

      <button type="button" className="add-column" onClick={handleAddColumn}>
        <span className="icon icon-plus" />
      </button>
    </div>
  )
}
